import { Box, Button, Checkbox, Flex, Link, Tag, Text } from "@chakra-ui/react";
import { useRouter } from "@uirouter/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { BodyOf } from "../../core/api";
import DataTable from "../../shared/components/DataTable/DataTable";
import useEndpointDataTable from "../../shared/components/DataTable/useEndpointDataTable";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { useEntityLink } from "../../shared/hooks/useEntityLink";
import { OfficeId, PatientId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import {
  PatientSearchSelection,
  usePatientSearchFilter,
} from "./components/usePatientSearchFilter";
import {
  CONCERNING_OPTIONS,
  ConcerningFilter,
  dateRangeForDueOnly,
  defaultDateRange,
  PATHWAY_OPTIONS,
  ListPreset,
  PatientGroup,
  ResultCount,
  Pathway,
  Priority,
} from "./dailyNotesShared";

type PageBody = BodyOf<"post", "./visit_daily_notes/patients/page">;

type Filters = {
  officeId: OfficeId | null;
  patientId: PatientId | null;
  priorities: Priority[] | null;
  reviewPathways: Pathway[] | null;
  concerning: ConcerningFilter | null;
  dueOnly: boolean;
  fromDate: LocalDate | null;
  toDate: LocalDate | null;
};

const columnHelper = createColumnHelper<PatientGroup>();

/**
 * The filters the list was last left with, so coming back from a patient's view -- the Back
 * button or the browser's -- finds the list as the nurse left it. Opening a patient replaces
 * the list, so its own state does not survive the trip.
 *
 * Kept per preset: a Dashboard tile opens the list with its own filters, and a different tile
 * must not bring back what the last one was narrowed to. In memory rather than in storage, so a
 * reload starts afresh instead of reviving a date window from days ago.
 */
let rememberedFilters: {
  presetKey: string;
  filters: Filters;
  patient: PatientSearchSelection | null;
} | null = null;

/**
 * The patients with daily notes waiting for review: the page a nurse starts from.
 *
 * One row per patient and not one per note, because a nurse reviews one patient at a time --
 * an attestation covers one participant -- so the unit of work is a patient. Each row says how
 * much is waiting and how urgent it is; opening it drops into that patient's own view.
 *
 * Only notes no attestation covers yet are counted. Every patient in the rollout is listed,
 * including those with nothing waiting (a count of 0), so anyone's History is always one click
 * away. The "Concerning only" and "Due only" filters hide patients with no matching note.
 *
 * Opens on the last month. Ticking "Due only" widens the window to reach the older notes that
 * are due, and unticking it goes back to the last month.
 */
export default function DailyNotePatientsList(props: { preset: ListPreset | null }) {
  const { api, queries } = useApi();
  const entityLink = useEntityLink();
  const { stateService } = useRouter();

  const presetKey = JSON.stringify(props.preset);
  // Read once, on mount: what the list opens with, not something to follow afterwards.
  const [restored] = React.useState(() =>
    rememberedFilters?.presetKey === presetKey ? rememberedFilters : null
  );

  const offices = useQuery(queries.dailyNotes.reviewOffices());
  const patientSearch = usePatientSearchFilter(restored?.patient ?? null);
  const [totalNoteCount, setTotalNoteCount] = React.useState(0);

  const openPatient = React.useCallback(
    (patientId: PatientId, view: "review" | "all") =>
      stateService.go("app.visits.dailyNotes", { notesPatient: patientId, notesView: view }),
    [stateService]
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("patientName", {
        header: "Patient",
        cell: ({ row, getValue }) => (
          // align="flex-start": a column flex stretches its children, which made the link as
          // wide as the cell, so hovering empty space beside the name looked clickable and
          // opened the profile instead of the row's review view.
          <Flex align="flex-start" direction="column">
            <Link
              color="blue.600"
              onClick={(event) => {
                event.stopPropagation();
                entityLink.open({
                  event,
                  entity: { type: "Patient", id: row.original.patientId },
                });
              }}
            >
              {getValue()}
            </Link>
            {row.original.patientExternalId === null ? null : (
              <Text color="gray.500" fontSize="xs">
                ID {row.original.patientExternalId}
              </Text>
            )}
          </Flex>
        ),
      }),
      columnHelper.accessor("noteCount", {
        header: "To review",
        cell: (info) =>
          info.getValue() === 0 ? (
            <Text color="gray.500">0</Text>
          ) : (
            <Text fontWeight="semibold">{info.getValue()}</Text>
          ),
      }),
      columnHelper.display({
        id: "_priorities",
        header: "By priority",
        cell: ({ row }) => (
          <Flex gap={1} wrap="wrap">
            {row.original.urgentCount > 0 ? (
              <Tag colorScheme="red" size="sm">
                {row.original.urgentCount} urgent
              </Tag>
            ) : null}
            {row.original.highCount > 0 ? (
              <Tag colorScheme="orange" size="sm">
                {row.original.highCount} high
              </Tag>
            ) : null}
            {row.original.routineCount > 0 ? (
              <Tag colorScheme="gray" size="sm">
                {row.original.routineCount} non-concerning
              </Tag>
            ) : null}
            {/* Not classified yet, so no priority to count it under. Said plainly so a
                patient does not read as cleaner than they are while a note is unread. */}
            {row.original.unclassifiedCount > 0 ? (
              <Tag size="sm" variant="outline">
                {row.original.unclassifiedCount} analysing
              </Tag>
            ) : null}
          </Flex>
        ),
      }),
      columnHelper.accessor("dueCount", {
        header: "Due",
        cell: (info) =>
          info.getValue() === 0 ? (
            <Text color="gray.500">—</Text>
          ) : (
            <Text color="red.700" fontWeight="semibold">
              {info.getValue()}
            </Text>
          ),
      }),
      columnHelper.accessor("oldestVisitDate", {
        header: "Oldest visit",
        cell: (info) => {
          const oldest = info.getValue();

          return oldest === null ? (
            <Text color="gray.500">—</Text>
          ) : (
            <Text whiteSpace="nowrap">{dateFormatter.toDate(oldest)}</Text>
          );
        },
      }),
      columnHelper.display({
        id: "_actions",
        header: "",
        // stopPropagation: the row opens the patient's view, and these should not also do that.
        cell: ({ row }) => (
          <Flex gap={2} justify="flex-end">
            <Button
              colorScheme="blue"
              isDisabled={row.original.noteCount === 0}
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                openPatient(row.original.patientId, "review");
              }}
            >
              Review
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(event) => {
                event.stopPropagation();
                openPatient(row.original.patientId, "all");
              }}
            >
              History
            </Button>
          </Flex>
        ),
      }),
    ],
    [entityLink, openPatient]
  );

  const { table, query, totalCount, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["visitDailyNotes", "patients", "page"],
    columns: columns,
    columnVisiblity: {
      storage: { key: "visits/dailyNotes/patients", version: 1 },
    },
    filters: {
      initialState:
        restored?.filters ??
        ({
          officeId: props.preset?.officeId ?? null,
          patientId: null,
          reviewPathways: null,
          ...(props.preset === null
            ? {
                priorities: null,
                concerning: null,
                dueOnly: false,
                ...defaultDateRange(),
              }
            : {
                priorities: null,
                concerning: props.preset.concerning,
                dueOnly: props.preset.dueOnly,
                fromDate: props.preset.fromDate,
                toDate: props.preset.toDate,
              }),
        } satisfies Filters as Filters),
    },
    fetchPage: async (request) => {
      const body: PageBody = {
        officeId: request.filters.officeId,
        patientId: request.filters.patientId,
        priorities: request.filters.priorities ?? [],
        reviewPathways: request.filters.reviewPathways ?? [],
        concerning: request.filters.concerning,
        dueOnly: request.filters.dueOnly,
        fromDate: request.filters.fromDate,
        toDate: request.filters.toDate,
        limit: request.limit,
        offset: request.offset,
      };

      const page = await api.post("./visit_daily_notes/patients/page", { body });

      // The table only carries rows and a patient count; the number of notes behind them rides
      // along in the same response and is kept for the heading.
      setTotalNoteCount(page.totalNoteCount);

      return page;
    },
    fetchAllForExport: async () => [],
  });

  React.useEffect(() => {
    rememberedFilters = { presetKey, filters, patient: patientSearch.selected };
  }, [presetKey, filters, patientSearch.selected]);

  const filterNode = (
    <Flex gap={3} wrap="wrap">
      <Select
        allowUnselect={true}
        isLoading={offices.isPending}
        label="Office"
        multiple={false}
        options={(offices.data ?? []).map((office) => ({
          label: office.name,
          value: office.id,
        }))}
        value={filters.officeId}
        onChange={(x) => setFilter("officeId", x ?? null)}
      />

      <Select
        allowUnselect={true}
        controlledSearchTerm={[patientSearch.search, patientSearch.setSearch]}
        isLoading={patientSearch.isFetching}
        label="Patient"
        multiple={false}
        options={patientSearch.options.map((patient) => ({
          label: patient.label,
          value: patient.id,
        }))}
        searchable={true}
        value={filters.patientId}
        onChange={(x) => {
          patientSearch.remember(x);
          setFilter("patientId", x ?? null);
        }}
      />

      <SingleDatePicker
        placeholderText="Visit from"
        selected={filters.fromDate}
        onChange={(date) => setFilter("fromDate", date)}
      />

      <SingleDatePicker
        placeholderText="Visit to"
        selected={filters.toDate}
        onChange={(date) => setFilter("toDate", date)}
      />

      <Select
        label="Pathway"
        multiple={true}
        options={PATHWAY_OPTIONS}
        value={filters.reviewPathways}
        onChange={(x) => setFilter("reviewPathways", (x as Pathway[] | null) ?? null)}
      />

      <Select
        allowUnselect={true}
        label="Concerning"
        multiple={false}
        options={CONCERNING_OPTIONS}
        value={filters.concerning}
        onChange={(x) => setFilter("concerning", x ?? null)}
      />

      <Checkbox
        alignSelf="center"
        isChecked={filters.dueOnly}
        onChange={(event) => {
          const range = dateRangeForDueOnly(event.target.checked);
          setFilter("dueOnly", event.target.checked);
          setFilter("fromDate", range.fromDate);
          setFilter("toDate", range.toDate);
        }}
      >
        Due only
      </Checkbox>
    </Flex>
  );

  return (
    <Box>
      {/* Trimmed from the default: Page.Header pads 8 and Page.Content pads 6, which
          puts ~56px of nothing between the heading and the first filter. */}
      <Page.Header pb={2}>
        <Flex align="center" gap={3}>
          <Page.Title>Daily Notes</Page.Title>
          <ResultCount count={totalCount} isLoading={query.isPending} noun="patient" />
          <ResultCount count={totalNoteCount} isLoading={query.isPending} noun="note" />
        </Flex>
      </Page.Header>

      <Page.Content pt={1}>
        <DataTable
          filterNode={filterNode}
          isLoading={query.isPending}
          metaButtonProps={metaButtonProps}
          spacing="tight"
          table={table}
          onClickRow={(_event, row) =>
            // Nothing to review opens the history rather than an empty review table.
            openPatient(row.original.patientId, row.original.noteCount === 0 ? "all" : "review")
          }
        />
      </Page.Content>
    </Box>
  );
}
